import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, LogOut, Wifi, WifiOff } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { Brand, LangSwitch } from "@/components/Shell";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export function WorkerShell({ children, title }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { t } = useT();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return (
    <div className="min-h-screen bg-ink text-white grain relative">
      <header className="relative sticky top-0 z-40 flex items-center justify-between px-4 h-16 border-b border-white/10 bg-ink/90 backdrop-blur-md">
        <Brand dark />
        <div className="flex items-center gap-3">
          <LangSwitch dark />
          <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${online ? "text-emerald-400" : "text-amber-400"}`} data-testid="online-indicator">{online ? <Wifi size={14} /> : <WifiOff size={14} />}{online ? "online" : "offline"}</span>
          <button data-testid="worker-logout" onClick={async () => { await logout(); nav("/login"); }} className="grid h-10 w-10 place-items-center rounded-full bg-white/10"><LogOut size={16} /></button>
        </div>
      </header>
      <main className="relative px-4 py-6 pb-32 max-w-lg mx-auto">
        {title && <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-1">{user?.name}</p>}
        {title && <h1 className="font-serif text-4xl font-bold mb-6">{title}</h1>}
        {children}
      </main>
    </div>
  );
}

export default function WorkerHome() {
  const { t } = useT();
  const [jobs, setJobs] = useState(null);
  useEffect(() => { api.get("/requests").then((r) => setJobs(r.data)); }, []);
  const active = jobs?.filter((j) => ["confermato", "in_corso"].includes(j.status)).sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || "")) || [];
  const done = jobs?.filter((j) => !["confermato", "in_corso"].includes(j.status)) || [];
  return (
    <WorkerShell title={t("worker.myJobs")}>
      <div className="space-y-4" data-testid="worker-jobs">
        {jobs && active.length === 0 && <div className="rounded-2xl border border-white/15 p-8 text-center text-white/60" data-testid="worker-empty">{t("worker.none")}</div>}
        {active.map((j) => (
          <Link key={j.id} to={`/operaio/${j.id}`} data-testid={`worker-job-${j.number}`} className={`block rounded-2xl p-5 border-l-[6px] bg-white/[0.06] transition-transform active:scale-[0.98] ${j.status === "in_corso" ? "border-emerald-400" : "border-safety"}`}>
            <div className="flex items-center justify-between text-xs font-mono text-white/50"><span>{j.number}</span><span className={j.status === "in_corso" ? "text-emerald-400 font-bold" : "text-safety font-bold"}>{j.status === "in_corso" ? t("worker.inProgress") : t("worker.todo")}</span></div>
            <h2 className="mt-2 font-serif text-2xl font-semibold">{j.service_name}</h2>
            <p className="mt-2 flex items-center gap-2 text-sm text-white/70"><MapPin size={14} /> {j.address}, {j.city}</p>
            <p className="mt-1 flex items-center gap-2 text-sm text-white/70"><Clock size={14} /> {fmtDate(j.scheduled_date, true)}</p>
          </Link>
        ))}
        {done.length > 0 && <>
          <h2 className="pt-6 text-xs uppercase tracking-[0.2em] text-white/40">{t("worker.completed")}</h2>
          {done.map((j) => <Link key={j.id} to={`/operaio/${j.id}`} className="block rounded-xl p-4 bg-white/[0.04] text-sm text-white/60"><span className="font-mono text-xs">{j.number}</span> · {j.service_name} · {j.city}</Link>)}
        </>}
      </div>
    </WorkerShell>
  );
}
